// Report Composer — 결재란(approvalBlock special) 데이터 빌더의 **단일 코어** (2026-09-18 R6: web → rc 승격).
//
// 네 surface(web 인증 상세 `Report` / 공유 `ShareReportResponse` / 외부 결재 `ExternalSignatureReportView` /
// admin 상세 `/admin/reports/:id`)가 같은 규칙을 각자 적으면 한쪽만 고쳐진다(F-25·F-34가 그렇게 새어
// 나갔고, admin은 앱 경계 때문에 「부분 미러」로 살았다) — surface별 빌더는 자기 응답을
// {@link ApprovalBlockSource}로 좁혀 이 함수를 부를 뿐이다.
//
//   - approvalLines: 정의 기반(approvalLineDefinition) → {@link approvalLinesFromDefinition}
//   - approvalSignatures: 최신 round 서명(숨김 상태 반영). 반려 뒤 재작성 구간(F-34)은 0건
//   - author*: 작성 완료 서명이 채우는 칸(작성자 단계 + 위임 담당 칸) — 표시 게이트는
//     shouldHideAuthoringSignature(F-25)
//   - offlineSignature: approvalSignatureMode === 'OFFLINE'
//
// ⚠️ web `composer/composerApprovalData`는 상세 spec 8개가 팩토리 mock한다 — 그 파일에 export를 더하면
// 그 spec들이 죽는다(`memory/feedback-pure-predicate-must-not-live-in-a-vi-mocked-module`). 코어·술어가
// rc(이 파일·`authoringSignatureVisibility`)에 사는 이유다.

import {
  isApprovalSlotUnassigned,
  latestRoundSignatures,
  type ApprovalLineDefinitionRow,
  type ExternalApprovalLineDefinitionRow,
} from '@safetyhub/shared';
import type {
  ApprovalBlockData,
  ApprovalLineItem,
  ApprovalSignatureDisplay,
} from './types';
import {
  isRewrittenAfterRejection,
  shouldHideAuthoringSignature,
  shouldHideSignatures,
} from './authoringSignatureVisibility';

/**
 * 작성 완료 서명 — 네 계약의 공통 형상. 공개 계약(shared `ShareReportAuthorSignature`)이 이 형상
 * 그대로이고, 인증 `Report`·admin 상세의 flat `authorSignerName/authorSignatureData/authorSignedAt`은
 * 각 앱의 어댑터가 이 객체로 모은다(셋 다 없으면 null — 서버 조립 규칙과 같다).
 */
export interface ApprovalBlockAuthorSignature {
  signerName?: string | null;
  /** 담당자가 「작성 완료」를 누른 시각 — 표시 게이트(F-25)·재작성 판정(F-34)이 읽는다. */
  signedAt?: string | Date | null;
  signatureData?: string | null;
}

/** 결재 서명 행 — 네 계약의 공통 최소 형상(`comment`는 어느 surface도 읽지 않는다 — 계약 §3). */
export interface ApprovalBlockSignatureSource {
  /** 인증 상세·admin 상세만 갖는다. 공개 응답은 인덱스 기반 표시 id로 대체한다. */
  id?: string;
  approvalLevel: number;
  status: string;
  signerName?: string | null;
  signerTitle?: string | null;
  stepLabel?: string | null;
  signedAt?: string | Date | null;
  signatureData?: string | null;
  round?: number | null;
}

/**
 * 네 surface가 공통으로 넘기는 최소 형상. `status` 외엔 전부 optional — 계약마다 optional 여부가
 * 달라(`Report.approvalSignatures?` / `ExternalSignatureReportView.approvalLineDefinition?`) required로
 * 두면 optional 쪽이 구조적으로 안 맞는다. 값 부재는 전부 「없음」으로 읽는다.
 */
export interface ApprovalBlockSource {
  status: string;
  approvalLineDefinition?:
    | ApprovalLineDefinitionRow[]
    | ExternalApprovalLineDefinitionRow[]
    | null;
  approvalSignatures?: ReadonlyArray<ApprovalBlockSignatureSource> | null;
  authorSignature?: ApprovalBlockAuthorSignature | null;
  /** 위임 담당자 — **존재 여부**만 읽는다(`isDelegatedReport`). 계약마다 형상이 달라 unknown. */
  delegatedContact?: unknown;
  approvalSignatureMode?: string | null;
}

/**
 * 보고서 정의(approvalLineDefinition 컬럼)를 결재란 표시 라인({@link ApprovalLineItem})으로 변환한다.
 * surface 통일(2026-06-10): 상세/print/공유/admin 모두 이 정의로 결재란을 채워 동일한 칸을 그린다.
 *
 * - level 오름차순 정렬. author 단계는 sourceType==='author'로 식별 → 결재란 special이 작성자 서명을 매핑.
 * - sourceType 없는 구 정의는 assignmentChain[0].type으로 fallback 판별(서버 getReportApprovalLines와 동일).
 * - 내부(상세/admin)는 `ApprovalLineDefinitionRow`, 외부(공유/외부 결재) 응답은
 *   `ExternalApprovalLineDefinitionRow`(memberId/assignmentChain/notifyVia omit)를 넘긴다 — 표시에 필요한
 *   필드만 읽으므로 union으로 받는다.
 * - 정의가 없는 구 보고서는 빈 배열 → 결재란 special이 합성 1칸 fallback을 그린다.
 *
 * `_assigneeUnset`(담당자 미지정 칸 = 오프라인 수기 결재)은 이 변환이 채운다. 이 값이 없으면 렌더의
 * 이름 폴백(`buildMergedApprovalItems` member→**label**)이 살아나 「결재」 칸의 결재자 이름이 「결재」로
 * 표시되고(2026-08-13 dev 실측), 제출 후엔 서버 폴백(PARKED_SLOT_SIGNER_NAME)이 떠 출력물의 빈 서명란이
 * 사라진다(= 수기 결재 불가). 근거: 결정 2026-08-13 §3.
 *
 *   - internal row는 서버 SSOT와 같은 함수(`isApprovalSlotUnassigned` — memberId 없음 ∧ 빈 체인)로
 *     계산한다. memberName 부재로 되짚으면 체인은 있으나 resolve만 실패한 칸(role/delegatedTo)까지
 *     미지정으로 오인한다.
 *   - ⚠️ external row(공유/외부결재)는 **판정 원료가 응답에서 제거돼 있다** — memberId·assignmentChain이
 *     전부 없어 그대로 계산하면 모든 칸이 미지정으로 판정되고, 렌더러가 이름을 빈 문자열로 눌러
 *     공유·PDF의 결재자 이름이 통째로 사라진다(2026-08-26 dev 실측). 그래서 서버가 실어 보낸 판정 결과
 *     (`ExternalApprovalLineDefinitionRow.unassigned`)를 그대로 쓴다. 플래그가 없는 응답(구 서버)만
 *     종전 계산으로 폴백한다.
 */
export function approvalLinesFromDefinition(
  definition:
    | ApprovalLineDefinitionRow[]
    | ExternalApprovalLineDefinitionRow[]
    | null
    | undefined,
): ApprovalLineItem[] {
  if (!definition || definition.length === 0) return [];
  return [...definition]
    .sort((a, b) => a.level - b.level)
    .map((row) => {
      // external row에는 memberId/assignmentChain/notifyVia가 없다.
      const internal = row as Partial<ApprovalLineDefinitionRow>;
      const external = row as Partial<ExternalApprovalLineDefinitionRow>;
      return {
        approvalLevel: row.level,
        label: row.label,
        sourceType: row.sourceType ?? internal.assignmentChain?.[0]?.type ?? undefined,
        member: row.memberName
          ? { id: '', name: row.memberName, title: row.memberTitle ?? null }
          : undefined,
        _assigneeUnset: external.unassigned ?? isApprovalSlotUnassigned(internal),
      };
    });
}

type SigStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

/** 응답의 문자열 status를 표시 상태로 좁힌다(미지의 값은 PENDING). */
function narrowStatus(status: string): SigStatus {
  return status === 'APPROVED'
    ? 'APPROVED'
    : status === 'REJECTED'
      ? 'REJECTED'
      : 'PENDING';
}

/** 계약의 날짜 필드(string|Date|null)를 표시용 문자열|null로 정규화한다. */
function toIsoOrNull(value: string | Date | null | undefined): string | null {
  if (value == null) return null;
  return value instanceof Date ? value.toISOString() : value;
}

/**
 * 최신 round 결재 서명만 추려 표시 입력으로 변환한다.
 * 진행 중/반려 등 서명 숨김 상태(shouldHideSignatures)면 status를 PENDING으로 강등 + 서명/시각 제거.
 */
function buildApprovalSignatures(source: ApprovalBlockSource): ApprovalSignatureDisplay[] {
  const sigs = source.approvalSignatures ?? [];
  if (sigs.length === 0) return [];
  /**
   * 반려 뒤 담당자가 다시 작성 완료한 구간(F-34)은 **제출 전 모양**이다 — 반려된 라운드의 행을
   * 아예 넘기지 않는다(PENDING으로 강등만 하면 rc 매핑이 `!sig`를 못 지나 담당 칸에 새 작성
   * 서명이 안 실린다). 그 라운드의 승인·반려는 결재 이력이 보여 준다. 근거는 술어 JSDoc.
   */
  if (
    isRewrittenAfterRejection({
      status: source.status,
      authorSignedAt: source.authorSignature?.signedAt,
      approvalSignatures: sigs,
    })
  ) {
    return [];
  }
  const hideSign = shouldHideSignatures(source.status);
  // 최신 round 필터는 shared 정본 하나(`latestRoundSignatures`, `?? 1`) — web 6곳의 `||1`/`??1` 혼재를 접었다(라운드 6).
  return latestRoundSignatures(sigs).map((s, i) => ({
    id: s.id ?? `approval-sig-${i}`,
    approvalLevel: s.approvalLevel,
    status: hideSign ? 'PENDING' : narrowStatus(s.status),
    signerName: s.signerName ?? null,
    signerTitle: s.signerTitle ?? null,
    stepLabel: s.stepLabel ?? null,
    signedAt: hideSign ? null : toIsoOrNull(s.signedAt),
    signatureData: hideSign ? null : (s.signatureData ?? null),
  }));
}

/**
 * Composer 결재란(approvalBlock special)에 주입할 ApprovalBlockData를 빌드한다.
 * variant는 node.props.variant가 우선이므로 여기서는 지정하지 않는다(ApprovalBlockSpecial 기본).
 *
 * ⚠️ author* 숨김은 `shouldHideSignatures`(DRAFT인가)가 아니라
 * {@link shouldHideAuthoringSignature}가 정한다 — **위임본은 DRAFT 구간에도 확정된 작성
 * 서명을 가진다**(담당자가 외부 링크에서 작성 완료). 사유는 그 모듈 JSDoc(F-25). 확정 전 인쇄
 * 미리보기(`composed-print-preview`)가 DRAFT를 공유 응답으로 이 빌더에 넘기므로 공개 surface에도
 * 같은 규칙이 필요하다.
 */
export function buildApprovalBlockData(source: ApprovalBlockSource): ApprovalBlockData {
  const hideAuthorSign = shouldHideAuthoringSignature({
    status: source.status,
    delegatedContact: source.delegatedContact,
    authorSignedAt: source.authorSignature?.signedAt,
  });
  return {
    approvalLines: approvalLinesFromDefinition(source.approvalLineDefinition),
    approvalSignatures: buildApprovalSignatures(source),
    authorSignerName: hideAuthorSign
      ? undefined
      : (source.authorSignature?.signerName ?? undefined),
    authorSignatureData: hideAuthorSign
      ? undefined
      : (source.authorSignature?.signatureData ?? undefined),
    offlineSignature: source.approvalSignatureMode === 'OFFLINE',
  };
}
